import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import CreatableSelect from "react-select/creatable";
import "../../styles/SchedulerManagementPage.css";

const DEFAULT_SETTINGS = {
  timezone: "America/Edmonton",
  expirySync: { enabled: true, time: "03:00" },
  notifications: { enabled: true, time: "03:00" },
  expirationReport: {
    enabled: false,
    frequency: "twice_monthly",
    time: "03:00",
    weeklyDay: "monday",
    monthlyDay: "15",
    firstDay: "15",
    secondDay: "last",
    recipientUserIds: [],
    recipientEmails: [],
  },
};

const FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "twice_monthly", label: "Twice Monthly" },
];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ordinalDay = (day) => {
  const value = Number(day);
  const suffix = value % 10 === 1 && value % 100 !== 11 ? "st"
    : value % 10 === 2 && value % 100 !== 12 ? "nd"
      : value % 10 === 3 && value % 100 !== 13 ? "rd" : "th";
  return `${value}${suffix}`;
};
const MONTH_DAYS = [
  ...Array.from({ length: 31 }, (_, index) => ({
    value: String(index + 1),
    label: ordinalDay(index + 1),
  })),
  { value: "last", label: "Last day of the month" },
];

const uniqueRecipientUserIds = (values) => [
  ...new Set((Array.isArray(values) ? values : [])
    .map(Number)
    .filter(Number.isInteger)),
];

const uniqueRecipientEmails = (values) => [
  ...new Set((Array.isArray(values) ? values : [])
    .map((email) => String(email || "").trim().toLowerCase())
    .filter(Boolean)),
];

const normalizePayloadSettings = (value = {}) => ({
  ...DEFAULT_SETTINGS,
  ...value,
  expirySync: { ...DEFAULT_SETTINGS.expirySync, ...(value.expirySync || {}) },
  notifications: { ...DEFAULT_SETTINGS.notifications, ...(value.notifications || {}) },
  expirationReport: {
    ...DEFAULT_SETTINGS.expirationReport,
    ...(value.expirationReport || {}),
    recipientUserIds: uniqueRecipientUserIds(value.expirationReport?.recipientUserIds),
    recipientEmails: uniqueRecipientEmails(value.expirationReport?.recipientEmails),
  },
});

const statusLabel = (run) => {
  if (!run) return "Not Run Yet";
  if (run.status === "success") return "Successful";
  if (run.status === "partial") return "Partially Successful";
  if (run.status === "failed") return "Failed";
  return "Running";
};

export default function SchedulerManagementPage() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  }), []);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadScheduler = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${BASE_URL}/api/scheduler`, { headers });
      setSettings(normalizePayloadSettings(data.settings));
      setPreview(data.preview || null);
      setStatus(data.status || null);
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to load scheduler settings.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, headers]);

  useEffect(() => { loadScheduler(); }, [loadScheduler]);

  const userOptions = useMemo(() => users.map((user) => ({
    value: `user:${Number(user.id)}`,
    userId: Number(user.id),
    email: String(user.email || "").trim().toLowerCase(),
    label: `${user.name} (${user.email})`,
  })), [users]);
  const selectedUserRecipients = userOptions.filter((option) =>
    settings.expirationReport.recipientUserIds.includes(option.userId));
  const selectedUserEmails = new Set(selectedUserRecipients.map((option) => option.email));
  const selectedRecipients = [
    ...selectedUserRecipients,
    ...uniqueRecipientEmails(settings.expirationReport.recipientEmails)
      .filter((email) => !selectedUserEmails.has(email))
      .map((email) => ({
      value: `email:${email}`,
      email,
      label: email,
    })),
  ];

  const updateJob = (job, field, value) => {
    setSettings((current) => ({
      ...current,
      [job]: { ...current[job], [field]: value },
    }));
  };

  const saveScheduler = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const { data } = await axios.put(`${BASE_URL}/api/scheduler`, settings, { headers });
      setSettings(normalizePayloadSettings(data.settings || settings));
      setPreview(data.preview || preview);
      setMessage(data.message || "Scheduler settings updated successfully.");
      await loadScheduler();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to save scheduler settings.");
    } finally {
      setSaving(false);
    }
  };

  const notificationRun = status?.notifications;
  const expirySyncRun = status?.expirySync;
  const reportRun = status?.expirationReport;

  return (
    <div className="account-settings-tab-panel scheduler-management-page">
      <div className="account-settings-tab-header">
        <div>
          <h2>Scheduler Management</h2>
          <p>Manage automatic notification emails and the expiration report schedule.</p>
        </div>
        <button type="submit" form="scheduler-management-form" className="btn btn-primary" disabled={saving || loading}>
          <i className="fas fa-save" aria-hidden="true" /> {saving ? "Saving..." : "Save Schedule"}
        </button>
      </div>

      {message ? <div className="scheduler-message">{message}</div> : null}
      {error ? <div className="scheduler-message error">{error}</div> : null}
      {loading ? <div className="scheduler-loading">Loading scheduler settings...</div> : (
        <form id="scheduler-management-form" onSubmit={saveScheduler}>
          <section className="scheduler-config-section">
            <div className="scheduler-section-heading">
              <span className="scheduler-section-icon"><i className="fas fa-sync-alt" aria-hidden="true" /></span>
              <div><h3>Expiry Status Sync</h3><p>Updates expired document-task and training statuses once each day.</p></div>
              <label className="scheduler-toggle"><input type="checkbox" checked={settings.expirySync.enabled} onChange={(event) => updateJob("expirySync", "enabled", event.target.checked)} /><span>Enabled</span></label>
            </div>
            <div className="scheduler-field-grid scheduler-single-field">
              <label><span>Run Time</span><input type="time" value={settings.expirySync.time} onChange={(event) => updateJob("expirySync", "time", event.target.value)} required /></label>
            </div>
          </section>

          <section className="scheduler-config-section">
            <div className="scheduler-section-heading">
              <span className="scheduler-section-icon"><i className="fas fa-bell" aria-hidden="true" /></span>
              <div><h3>Notifications / Alerts Email</h3><p>Runs the existing candidate, associate, and administrator notification email process.</p></div>
              <label className="scheduler-toggle"><input type="checkbox" checked={settings.notifications.enabled} onChange={(event) => updateJob("notifications", "enabled", event.target.checked)} /><span>Enabled</span></label>
            </div>
            <div className="scheduler-field-grid scheduler-single-field">
              <label><span>Send Time</span><input type="time" value={settings.notifications.time} onChange={(event) => updateJob("notifications", "time", event.target.value)} required /></label>
            </div>
          </section>

          <section className="scheduler-config-section">
            <div className="scheduler-section-heading">
              <span className="scheduler-section-icon"><i className="fas fa-file-excel" aria-hidden="true" /></span>
              <div><h3>Expiration Excel Report Email</h3><p>Sends the Org Unit grouped workbook using the frequency, date, and time you choose.</p></div>
              <label className="scheduler-toggle"><input type="checkbox" checked={settings.expirationReport.enabled} onChange={(event) => updateJob("expirationReport", "enabled", event.target.checked)} /><span>Enabled</span></label>
            </div>
            <div className="scheduler-field-grid scheduler-report-grid">
              <label><span>Frequency</span><select value={settings.expirationReport.frequency} onChange={(event) => updateJob("expirationReport", "frequency", event.target.value)}>{FREQUENCIES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
              {settings.expirationReport.frequency === "weekly" ? (
                <label><span>Send Day</span><select value={settings.expirationReport.weeklyDay} onChange={(event) => updateJob("expirationReport", "weeklyDay", event.target.value)}>{WEEKDAYS.map((day) => <option key={day} value={day.toLowerCase()}>{day}</option>)}</select></label>
              ) : null}
              {settings.expirationReport.frequency === "monthly" ? (
                <label><span>Send Date</span><select value={settings.expirationReport.monthlyDay} onChange={(event) => updateJob("expirationReport", "monthlyDay", event.target.value)}>{MONTH_DAYS.map((day) => <option key={day.value} value={day.value}>{day.label}</option>)}</select></label>
              ) : null}
              {settings.expirationReport.frequency === "twice_monthly" ? (
                <>
                  <label><span>First Send Date</span><select value={settings.expirationReport.firstDay} onChange={(event) => updateJob("expirationReport", "firstDay", event.target.value)}>{MONTH_DAYS.map((day) => <option key={day.value} value={day.value}>{day.label}</option>)}</select></label>
                  <label><span>Second Send Date</span><select value={settings.expirationReport.secondDay} onChange={(event) => updateJob("expirationReport", "secondDay", event.target.value)}>{MONTH_DAYS.map((day) => <option key={day.value} value={day.value}>{day.label}</option>)}</select></label>
                </>
              ) : null}
              <label><span>Send Time</span><input type="time" value={settings.expirationReport.time} onChange={(event) => updateJob("expirationReport", "time", event.target.value)} required /></label>
              <div className="scheduler-recipient-field scheduler-recipient-wide">
                <label>Select User(s)</label>
                <CreatableSelect
                  isMulti
                  classNamePrefix="scheduler-select"
                  options={userOptions}
                  value={selectedRecipients}
                  onChange={(items) => {
                    const selected = items || [];
                    setSettings((current) => ({
                      ...current,
                      expirationReport: {
                        ...current.expirationReport,
                        recipientUserIds: uniqueRecipientUserIds(
                          selected.map((item) => item.userId)),
                        recipientEmails: uniqueRecipientEmails(
                          selected
                            .filter((item) => !Number.isInteger(item.userId))
                            .map((item) => item.email)),
                      },
                    }));
                  }}
                  getNewOptionData={(inputValue) => {
                    const email = inputValue.trim().toLowerCase();
                    return { value: `email:${email}`, label: email, email };
                  }}
                  isValidNewOption={(inputValue) => {
                    const email = inputValue.trim().toLowerCase();
                    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                      && !selectedRecipients.some((item) =>
                        String(item.email || "").toLowerCase() === email);
                  }}
                  formatCreateLabel={(inputValue) => `Add email: ${inputValue.trim()}`}
                  placeholder="Select a user or type an email address"
                  closeMenuOnSelect={false}
                />
              </div>
            </div>
          </section>

          <section className="scheduler-summary-section">
            <div className="scheduler-summary-heading"><h3>Current Schedule Summary</h3><p>Today: {status?.today || "-"} | Timezone: {preview?.timezone || settings.timezone}</p></div>
            <div className="scheduler-summary-grid">
              <article><span>Expiry Sync Schedule</span><strong>{preview?.expirySyncLabel || "Disabled"}</strong><small>Status: {statusLabel(expirySyncRun)}</small><small>Last run: {expirySyncRun?.timeLabel || "Not Run Yet"}</small></article>
              <article><span>Expiry Sync Today</span><strong>{expirySyncRun?.summary?.taskUpdatedCount ?? 0} task update(s)</strong><small>{expirySyncRun?.summary?.trainingUpdatedCount ?? 0} training update(s)</small><small>{expirySyncRun?.errorMessage || "Document and training expiry statuses"}</small></article>
              <article><span>Notifications Schedule</span><strong>{settings.notifications.enabled ? preview?.notificationsLabel : "Disabled"}</strong><small>Status: {statusLabel(notificationRun)}</small><small>Last run: {notificationRun?.timeLabel || "Not Run Yet"}</small></article>
              <article><span>Notification Emails Today</span><strong>{status?.notificationEmailsSentToday ?? 0}</strong><small>Scheduled run sent: {notificationRun?.summary?.emailsSent ?? notificationRun?.summary?.total ?? 0}</small><small>{notificationRun?.errorMessage || "Includes notification deliveries recorded today."}</small></article>
              <article><span>Expiration Report Schedule</span><strong>{preview?.expirationReportLabel || "-"}</strong><small>Status: {statusLabel(reportRun)}</small><small>Last run: {reportRun?.timeLabel || "Not Run Yet"}</small></article>
              <article><span>Expiration Report Today</span><strong>{reportRun?.summary?.emailsSent ?? 0} email(s) sent</strong><small>{reportRun?.summary?.recordCount ?? 0} record(s) | {reportRun?.summary?.orgUnitCount ?? 0} Org Unit(s)</small><small>{reportRun?.errorMessage || `${settings.expirationReport.recipientUserIds.length + settings.expirationReport.recipientEmails.length} configured recipient(s)`}</small></article>
            </div>
          </section>
        </form>
      )}
    </div>
  );
}
